'use client'

import { MultipleChoiceQuestion } from '@/api/services/types/questionTypes'
import { useTestResponse } from '@/hooks/useTestResponse'
import React, { useState } from 'react'
import { MultipleChoiceQuestionCard } from './MultipleChoiceQuestionCard'

interface MultipleQuestionTaskProps {
  data?: MultipleChoiceQuestion
  moduleId: string
  questionId: string
  onComplete: () => void
  onStart: () => void
}

// Przykładowe pytania do testów
const mockQuestions: MultipleChoiceQuestion[] = [
  {
    id: 'q1',
    questionText: 'Wybierz języki typowane statycznie:',
    requiredSelections: 3,
    timeLimitSeconds: 10,
    answers: [
      { id: 'js', questionId: 'q1', content: 'JavaScript', skillId: 'skill1', points: 0 },
      { id: 'ts', questionId: 'q1', content: 'TypeScript', skillId: 'skill1', points: 1 },
      { id: 'java', questionId: 'q1', content: 'Java', skillId: 'skill1', points: 1 },
      { id: 'cpp', questionId: 'q1', content: 'C++', skillId: 'skill1', points: 1 },
      { id: 'py', questionId: 'q1', content: 'Python', skillId: 'skill1', points: 0 }
    ]
  },
  {
    id: 'q2',
    questionText: 'Wybierz frameworki JavaScript:',
    requiredSelections: 3,
    timeLimitSeconds: 8,
    answers: [
      { id: 'react', questionId: 'q2', content: 'React', skillId: 'skill2', points: 1 },
      {
        id: 'angular',
        questionId: 'q2',
        content: 'Angular',
        skillId: 'skill2',
        points: 1
      },
      { id: 'vue', questionId: 'q2', content: 'Vue.js', skillId: 'skill2', points: 1 },
      { id: 'django', questionId: 'q2', content: 'Django', skillId: 'skill2', points: 0 },
      { id: 'flask', questionId: 'q2', content: 'Flask', skillId: 'skill2', points: 0 }
    ]
  },
  {
    id: 'q3',
    questionText: 'Wybierz systemy kontroli wersji:',
    requiredSelections: 3,
    timeLimitSeconds: 12,
    answers: [
      { id: 'git', questionId: 'q3', content: 'Git', skillId: 'skill3', points: 1 },
      { id: 'svn', questionId: 'q3', content: 'SVN', skillId: 'skill3', points: 1 },
      { id: 'docker', questionId: 'q3', content: 'Docker', skillId: 'skill3', points: 0 },
      {
        id: 'mercurial',
        questionId: 'q3',
        content: 'Mercurial',
        skillId: 'skill3',
        points: 1
      },
      {
        id: 'kubernetes',
        questionId: 'q3',
        content: 'Kubernetes',
        skillId: 'skill3',
        points: 0
      }
    ]
  }
]

interface Answer {
  questionId: string
  selectedIds: string[]
  responseTimeMillis: number
}

export default function MultipleQuestionTask({
  data,
  moduleId,
  questionId,
  onComplete,
  onStart
}: MultipleQuestionTaskProps) {
  const { addQuestionResponse } = useTestResponse()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([])
  const [startTime, setStartTime] = useState<number>(0)
  const [answers, setAnswers] = useState<Answer[]>([])

  React.useEffect(() => {
    handleStart()
  }, [])

  // Używamy danych z API lub mockowych danych
  const questions = data ? [data] : mockQuestions
  const currentQuestion = questions[currentIndex]

  const handleAnswer = (selectedIds: string[]) => {
    setSelectedAnswers(selectedIds)

    // Zapisz odpowiedź do lokalnego stanu
    setAnswers((prev) => [
      ...prev,
      {
        questionId: currentQuestion.id,
        selectedIds,
        responseTimeMillis: Date.now() - startTime
      }
    ])
  }

  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1)
      setSelectedAnswers([])
      setStartTime(Date.now())
    } else {
      // Na końcu zapisz wszystkie odpowiedzi do TestResponseProvider
      await addQuestionResponse(moduleId, {
        questionId,
        questionType: 'MULTIPLE_CHOICE_QUESTION',
        multipleChoiceAnswer: {
          selectedAnswerIds: answers.flatMap((answer) => answer.selectedIds),
          responseTimeMillis: Date.now() - startTime
        }
      })
      onComplete()
    }
  }

  const handleStart = () => {
    setStartTime(Date.now())
    onStart()
  }

  return (
    <div className="flex items-center justify-center">
      {
        <MultipleChoiceQuestionCard
          onAnswerChange={handleAnswer}
          onNext={handleNext}
          question={{
            id: currentQuestion.id,
            text: currentQuestion.questionText,
            choices: currentQuestion.answers.map((answer) => ({
              id: answer.id,
              label: answer.content
            })),
            maxSelections: currentQuestion.requiredSelections,
            requiredSelections: currentQuestion.requiredSelections,
            timeLimit: currentQuestion.timeLimitSeconds
          }}
          selectedAnswers={selectedAnswers}
        />
      }
    </div>
  )
}
